import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMediaQuery } from '@mantine/hooks'
import { Box, Group, Loader, Timeline, SimpleGrid } from '@mantine/core'
import { format, formatDistanceToNow } from 'date-fns'
import { IoBookmark } from 'react-icons/io5'
import agent from '../../app/api/agent'
import { useStore } from '../../app/stores/store'
import { Profile } from '../../app/models/profile'
import { Post } from '../../app/models/post'
import { Part } from '../../app/models/part'
import { FirearmBuild } from '../../app/models/build'
import type { UserFirearmDto } from '../../app/models/gunVault'
import {
  PostCard,
  EmptyState,
  AppButton,
  AppCard,
  AppStack,
  AppText,
  AmmoScoreTierCard,
  ProfileEmptyPanel,
  ProfileTabBar
} from '../../app/common/ui'
import ProfilePhotos from './ProfilePhotos'
import FollowList from './FollowList'
import BuildCardThumbnail from '../builds/BuildCardThumbnail'
import BuildVisibilityBadge from '../builds/BuildVisibilityBadge'
import { buildPath } from '../../app/utils/buildPath'
import { useFeatureFlag } from '../../app/hooks/useFeatureFlag'

const PROFILE_CTA_SX = {
  background: 'var(--gs-accent)',
  color: '#fff',
  fontFamily: 'DM Sans, sans-serif',
  '&:hover': { background: '#bf4a00' }
}

interface Props {
  profile: Profile;
  canEdit?: boolean;
}

function actionLabel (action: string): string {
  if (action === 'AdjustReputation') return 'Reputation adjustment'
  return action.replace(/([A-Z])/g, ' $1').trim() || action
}

function VaultCardReadOnly ({ item }: { item: UserFirearmDto }) {
  return (
    <AppCard
      withBorder
      padding="sm"
      radius="md"
      sx={{
        border: item.isPinned ? '2px solid var(--gs-gold)' : '1px solid var(--gs-border)',
        background: 'var(--gs-surface)',
        transition: 'box-shadow 200ms ease',
        '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,.15)' }
      }}
    >
      {item.coverImageUrl && (
        <Box mb="xs">
          <img
            src={item.coverImageUrl}
            alt={`${item.make} ${item.model}`}
            style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 8 }}
          />
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <AppText
            weight={600}
            size="sm"
            lineClamp={1}
            component={Link}
            to={`/registry/${item.firearmModelId}`}
            sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'var(--gs-accent)' } }}
          >
            {item.make} {item.model}
          </AppText>
          {item.caliber && (
            <AppText size="xs" color="dimmed" mt={2}>{item.caliber}</AppText>
          )}
        </Box>
        {item.isPinned && (
          <IoBookmark size={13} color="var(--gs-gold)" style={{ flexShrink: 0, marginLeft: 6 }} />
        )}
      </Box>
    </AppCard>
  )
}

export default observer(function ProfileContent ({ profile, canEdit }: Props) {
  const { profileStore } = useStore()
  const privacyOn = useFeatureFlag('builds-privacy-collaboration-enabled')
  const mobileTabs = useMediaQuery('(max-width: 768px)') ?? false
  const [activeTab, setActiveTab] = useState<string>('posts')
  const [posts, setPosts] = useState<Post[]>([])
  const [reviews, setReviews] = useState<Post[]>([])
  const [builds, setBuilds] = useState<FirearmBuild[]>([])
  const [parts, setParts] = useState<Part[]>([])
  const [vault, setVault] = useState<UserFirearmDto[] | null>(null)
  const [loadingPosts, setLoadingPosts] = useState(false)
  const [loadingReviews, setLoadingReviews] = useState(false)
  const [loadingBuilds, setLoadingBuilds] = useState(false)
  const [loadingParts, setLoadingParts] = useState(false)
  const [loadingVault, setLoadingVault] = useState(false)

  useEffect(() => {
    if (!profile.username) return
    setLoadingPosts(true)
    agent.Posts.list({ authorUsername: profile.username, pageSize: 20 })
      .then((r) => setPosts(r.items))
      .catch(() => setPosts([]))
      .finally(() => setLoadingPosts(false))
  }, [profile.username])

  const loadReviews = () => {
    if (reviews.length > 0) return
    setLoadingReviews(true)
    agent.Posts.list({ authorUsername: profile.username, type: 1, pageSize: 20 })
      .then((r) => setReviews(r.items))
      .catch(() => setReviews([]))
      .finally(() => setLoadingReviews(false))
  }

  const loadBuilds = () => {
    if (builds.length > 0) return
    setLoadingBuilds(true)
    agent.Builds.list({ ownerUsername: profile.username, pageSize: 20 })
      .then((r) => setBuilds(r.items))
      .catch(() => setBuilds([]))
      .finally(() => setLoadingBuilds(false))
  }

  const loadParts = () => {
    if (parts.length > 0) return
    setLoadingParts(true)
    agent.Parts.list({ authorUsername: profile.username, pageSize: 20 })
      .then((r) => setParts(r.items))
      .catch(() => setParts([]))
      .finally(() => setLoadingParts(false))
  }

  const loadVault = () => {
    if (vault !== null) return
    setLoadingVault(true)
    agent.Profiles.getPublicVault(profile.username)
      .then((items) => setVault(items))
      .catch(() => setVault([]))
      .finally(() => setLoadingVault(false))
  }

  const handleTabChange = (value: string) => {
    setActiveTab(value)
    if (value === 'reviews') loadReviews()
    if (value === 'builds') loadBuilds()
    if (value === 'parts') loadParts()
    if (value === 'vault') loadVault()
    if (value === 'activity' && profile.username) profileStore.loadActivityFeed(profile.username)
  }

  const mapPostToCard = (p: Post) => (
    <PostCard
      key={p.id}
      id={p.id}
      title={p.title}
      excerpt={p.excerpt}
      type={p.type}
      coverImageUrl={p.coverImageUrl}
      mainPhotoUrl={p.mainPhotoUrl}
      authorUsername={p.authorUsername}
      authorDisplayName={p.authorDisplayName}
      authorImage={p.authorImage}
      authorTier={p.authorAmmoTier}
      authorScore={p.authorAmmoScore}
      voteScore={p.voteScore ?? 0}
      currentUserVote={p.currentUserVote}
      commentCount={p.comments?.length ?? 0}
      createdAt={p.createdAt}
      tags={p.tags}
      variant="default"
    />
  )

  const tabs = [
    'posts',
    'reviews',
    'builds',
    'parts',
    'vault',
    'followers',
    'following',
    'about',
    'photos',
    ...(canEdit ? ['activity'] : [])
  ]

  return (
    <Box>
      <ProfileTabBar tabs={tabs} activeTab={activeTab} onChange={handleTabChange} mobile={mobileTabs} />

      <Box
        sx={{
          boxSizing: 'border-box',
          display: 'grid',
          gridTemplateColumns: '1fr 280px',
          gap: 'var(--gs-md)',
          padding: 'var(--gs-md)',
          '@media (max-width: 800px)': { gridTemplateColumns: '1fr' }
        }}
      >
        <Box>
          {activeTab === 'posts' && (
            loadingPosts
              ? <Loader size="sm" />
              : posts.length === 0
                ? (
                <ProfileEmptyPanel
                  title="Nothing posted yet"
                  message={canEdit ? 'Share your knowledge, reviews, or tips with the community.' : "This member hasn't posted yet."}
                  action={canEdit
                    ? (
                    <AppButton variant="filled" component={Link} to="/create-post" size="xs" sx={PROFILE_CTA_SX}>
                      Write a post
                    </AppButton>
                      )
                    : undefined}
                />
                  )
                : <AppStack spacing="sm">{posts.map(mapPostToCard)}</AppStack>
          )}

          {activeTab === 'reviews' && (
            loadingReviews
              ? <Loader size="sm" />
              : reviews.length === 0
                ? (
                <ProfileEmptyPanel
                  title="No reviews yet"
                  message={canEdit ? 'Tried a firearm or accessory? Share what you think.' : 'No reviews from this member yet.'}
                  action={canEdit
                    ? (
                    <AppButton variant="filled" component={Link} to="/create-post?type=1" size="xs" sx={PROFILE_CTA_SX}>
                      Write a review
                    </AppButton>
                      )
                    : undefined}
                />
                  )
                : <AppStack spacing="sm">{reviews.map(mapPostToCard)}</AppStack>
          )}

          {activeTab === 'builds' && (
            loadingBuilds
              ? <Loader size="sm" />
              : builds.length === 0
                ? (
                <ProfileEmptyPanel
                  title="No documented builds"
                  message={canEdit ? 'Document your builds and earn AmmoScore when the community votes them up.' : 'No builds shared yet.'}
                  action={canEdit
                    ? (
                    <AppButton variant="filled" component={Link} to="/builds/new" size="xs" sx={PROFILE_CTA_SX}>
                      Create a build
                    </AppButton>
                      )
                    : undefined}
                />
                  )
                : (
                <AppStack spacing="sm">
                  {builds.map((b) => (
                    <AppCard
                      key={b.id}
                      component={Link}
                      to={buildPath(b)}
                      padding="sm"
                      sx={{ textDecoration: 'none', color: 'inherit', background: 'var(--gs-surface)', border: '1px solid var(--gs-border)', borderRadius: 8 }}
                    >
                      <Group align="flex-start" spacing="sm" noWrap>
                        <BuildCardThumbnail url={b.mainPhotoUrl} alt={b.name || ''} variant="compact" />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Group spacing={6} noWrap>
                            <AppText weight={500} sx={{ fontFamily: 'Oswald, sans-serif' }}>{b.name || b.firearmModelName || 'Build'}</AppText>
                            {privacyOn && canEdit && (b.visibility !== 'Public' || b.isPublic === false) && (
                              <BuildVisibilityBadge build={b} />
                            )}
                          </Group>
                          <AppText size="xs" color="dimmed" mt={4}>
                            {b.firearmModelName ?? (b.firearmModelId ? 'Model pending review' : '—')} · {b.parts?.length ?? 0} parts · {b.voteScore ?? 0} votes · {format(new Date(b.updatedAt), 'MMM d, yyyy')}
                          </AppText>
                        </Box>
                      </Group>
                    </AppCard>
                  ))}
                </AppStack>
                  )
          )}

          {activeTab === 'parts' && (
            loadingParts
              ? <Loader size="sm" />
              : parts.length === 0
                ? (
                <ProfileEmptyPanel
                  title="No parts contributed"
                  message={canEdit ? 'Add parts to your bin and the community can upvote them for AmmoScore.' : 'No parts from this member yet.'}
                  action={canEdit
                    ? (
                    <AppButton variant="filled" component={Link} to="/parts-bin" size="xs" sx={PROFILE_CTA_SX}>
                      Head to Parts Bin
                    </AppButton>
                      )
                    : undefined}
                />
                  )
                : (
                <AppStack spacing="sm">
                  {parts.map((p) => (
                    <AppCard
                      key={p.id}
                      component={Link}
                      to="/parts-bin"
                      padding="sm"
                      sx={{ textDecoration: 'none', color: 'inherit', background: 'var(--gs-surface)', border: '1px solid var(--gs-border)', borderRadius: 8 }}
                    >
                      <AppText weight={500} size="sm">{p.name}</AppText>
                      <AppText size="xs" color="dimmed">{p.manufacturer} · {format(new Date(p.createdAt), 'MMM d, yyyy')}</AppText>
                    </AppCard>
                  ))}
                </AppStack>
                  )
          )}

          {activeTab === 'vault' && (
            loadingVault
              ? <Loader size="sm" />
              : vault === null
                ? null
                : vault.length === 0
                  ? (
                <ProfileEmptyPanel
                  title="Vault is private or empty"
                  message={canEdit ? 'Your vault is where you keep your firearms collection. Add your first gun.' : "This member's vault isn't public."}
                  action={canEdit
                    ? (
                    <AppButton variant="filled" component={Link} to="/gun-vault" size="xs" sx={PROFILE_CTA_SX}>
                      Go to your Vault
                    </AppButton>
                      )
                    : undefined}
                />
                    )
                  : (
                <SimpleGrid cols={1} breakpoints={[{ minWidth: 480, cols: 2 }, { minWidth: 720, cols: 3 }]} spacing="sm">
                  {vault.map((item) => (
                    <VaultCardReadOnly key={item.id} item={item} />
                  ))}
                </SimpleGrid>
                    )
          )}

          {activeTab === 'followers' && (
            <FollowList key={`followers-${profile.username}`} username={profile.username} mode="followers" />
          )}

          {activeTab === 'following' && (
            <FollowList key={`following-${profile.username}`} username={profile.username} mode="following" />
          )}

          {activeTab === 'about' && (
            <Box sx={{ padding: 'var(--gs-sm) 0' }}>
              {profile.bio && (
                <AppText sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 13, color: 'var(--gs-text)', whiteSpace: 'pre-wrap' }}>
                  {profile.bio}
                </AppText>
              )}
              <Box mt="md" sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 12, color: 'var(--gs-muted)' }}>
                <AppText>Total votes received: {profile.totalVotesReceived ?? 0}</AppText>
                <AppText>Approved submissions: {profile.approvedSubmissionsCount ?? 0}</AppText>
              </Box>
            </Box>
          )}

          {activeTab === 'photos' && (
            <Box sx={{ padding: 'var(--gs-sm) 0' }}>
              <ProfilePhotos profile={profile} />
            </Box>
          )}

          {activeTab === 'activity' && canEdit && (
            profileStore.loadingFeed
              ? <Loader size="sm" />
              : !profileStore.activityFeed?.items?.length
                  ? <EmptyState title="No activity yet" message="Reputation adjustments will appear here" />
                  : (
                <Timeline bulletSize={24} lineWidth={2} color="orange" mt="md">
                  {profileStore.activityFeed.items.map((item) => (
                    <Timeline.Item key={item.id}>
                      <AppText weight={600} size="sm" sx={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--gs-text)' }}>{actionLabel(item.action)}</AppText>
                      <AppText size="xs" sx={{ fontFamily: 'DM Sans, sans-serif', fontSize: 11, color: 'var(--gs-muted)' }}>{item.detail}</AppText>
                      <AppText size="xs" sx={{ color: 'var(--gs-muted)', marginTop: 'var(--gs-xs)' }}>
                        {formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })}
                        {item.scoreDelta != null && (
                          <AppText component="span" size="xs" weight={500} ml="xs" sx={{ color: item.scoreDelta >= 0 ? '#3FB950' : '#F85149' }}>
                            {item.scoreDelta >= 0 ? `+${item.scoreDelta}` : item.scoreDelta}
                          </AppText>
                        )}
                      </AppText>
                    </Timeline.Item>
                  ))}
                </Timeline>
                    )
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--gs-md)', '@media (max-width: 800px)': { display: 'none' } }}>
          <AmmoScoreTierCard
            score={profile.ammoScore ?? 0}
            totalVotesReceived={profile.totalVotesReceived}
            approvedSubmissionsCount={profile.approvedSubmissionsCount}
          />

          {canEdit && (
            <AppCard padding="md" sx={{ background: 'var(--gs-surface)', border: '1px solid var(--gs-border)', borderRadius: 8 }}>
              <AppText weight={600} size="xs" sx={{ fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', letterSpacing: 1, color: 'var(--gs-muted)', marginBottom: 12 }}>
                Quick Actions
              </AppText>
              <AppStack spacing="xs">
                <AppText component={Link} to={`/profiles/${profile.username}/edit`} size="sm" sx={{ color: 'var(--gs-accent)' }}>
                  Edit Profile
                </AppText>
                <AppText
                  component="button"
                  type="button"
                  size="sm"
                  sx={{ color: 'var(--gs-accent)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }}
                  onClick={() => handleTabChange('photos')}
                >
                  Upload Photo
                </AppText>
                <AppText
                  component="button"
                  type="button"
                  size="sm"
                  sx={{ color: 'var(--gs-accent)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }}
                  onClick={() => handleTabChange('activity')}
                >
                  View Activity
                </AppText>
                <AppText component={Link} to="/gun-vault" size="sm" sx={{ color: 'var(--gs-accent)' }}>
                  Manage Vault
                </AppText>
              </AppStack>
            </AppCard>
          )}
        </Box>
      </Box>
    </Box>
  )
})
